import { ExternalLink } from "@/components/ui/ExternalLink";
import { MediaImage } from "@/components/ui/MediaImage";
import { Reveal } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { TextLink } from "@/components/ui/Button";
import { linkSlot, visibleItems } from "@/components/cms/SectionRenderer";
import { useMediaByKey, useSettings } from "@/content-context";
import type { SectionDoc } from "@/content";
import { charityTrek } from "@/data/media";
import { imageReveal } from "@/lib/motion";

/**
 * Charity Trek, on the homepage. The one dark section on the page.
 *
 * The primary button leaves the site. It goes to the destination held in the
 * organisation's settings, which defaults to Mountain Routes' community page
 * and can be changed to an exact Charity Trek address without a deploy the
 * moment one is supplied.
 *
 * The disclosure line under the button is not boilerplate. Mountain Routes runs
 * the trekking half and operates that website; HISCUS runs the service half.
 * Sending a visitor to a partner's site without saying whose site it is would
 * imply an operating relationship that does not exist, which is the one thing
 * this project consistently refuses to do.
 */
export function CharityTrekBand({ section }: { section: SectionDoc | null }) {
  const settings = useSettings();
  const byKey = useMediaByKey();

  const trek = settings.charityTrek;
  const eyebrow = section?.eyebrow || "Get involved";
  const title = section?.title || "Trek. Serve. Sustain.";
  const lead =
    section?.lead ||
    "Trekking in Nepal with Mountain Routes, combined with service days on the trails, in schools and at community sites.";

  const activities = section?.items?.length
    ? visibleItems(section)
        .map((item) => item.title ?? "")
        .filter(Boolean)
    : ["Trail cleanup", "School maintenance", "Classroom painting", "Library development"];

  // Saved before the link was editable: keep the committed link rather than lose it.
  const guide = section?.ctas?.length
    ? linkSlot(section, 0)
    : { label: "How Charity Trek works with HISCUS", href: "/get-involved/charity-trek" };

  const images = (section?.mediaKeys?.length ? section.mediaKeys : ["ct-trail", "ct-scarf"])
    .map((key) => byKey(key))
    .filter(Boolean);

  const fallbackImages = [charityTrek[2], charityTrek[1]];
  const frames = images.length ? images : fallbackImages;

  return (
    <Section tone="midnight" ariaLabelledBy="trek-heading">
      <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="eyebrow text-sky/80">{eyebrow}</p>
          <h2 id="trek-heading" className="mt-4 text-white" style={{ fontSize: "var(--step-h2)" }}>
            {title}
          </h2>
          <p className="mt-5 text-white/70" style={{ fontSize: "var(--step-lead)" }}>
            {lead}
          </p>

          <ul className="mt-7 flex flex-wrap gap-x-3 gap-y-2 text-[0.9375rem] text-white/70">
            {activities.map((item, index) => (
              <li key={item} className="flex items-center gap-3">
                {index > 0 ? <span aria-hidden="true" className="h-1 w-1 rounded-full bg-sky/50" /> : null}
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ExternalLink href={trek.destinationUrl} variant="buttonLight">
              {trek.ctaLabel || "Explore Charity Trek"}
            </ExternalLink>
            {guide ? (
              <TextLink href={guide.href} tone="sky">
                {guide.label}
              </TextLink>
            ) : null}
          </div>

          <p className="mt-5 max-w-md text-[0.8125rem] leading-relaxed text-white/55">
            {trek.disclosure ||
              `Opens ${trek.partnerName} in a new tab. ${trek.partnerName} is the trekking partner and operates that website; HISCUS does not.`}
          </p>
        </Reveal>

        <Reveal variants={imageReveal} className="grid content-start gap-5">
          {frames.slice(0, 2).map((frame) => (
            <div
              key={frame!.id}
              className="relative aspect-[16/11] w-full overflow-hidden rounded-[24px] bg-white/5"
            >
              <MediaImage
                item={frame}
                fill
                sizes="(min-width: 1024px) 46vw, 100vw"
                className="object-cover"
              />
            </div>
          ))}
        </Reveal>
      </div>
    </Section>
  );
}
